import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { queryKeys } from './queryKeys'

export const AGENT_INSTRUCTION_KINDS = [
  'product_selection',
  'design',
  'agent',
  'email_inbox',
] as const

export type AgentInstructionKind = (typeof AGENT_INSTRUCTION_KINDS)[number]

export type AgentInstruction_ =
  Database['public']['Tables']['agent_instruction']['Row']
export type InsertAgentInstruction =
  Database['public']['Tables']['agent_instruction']['Insert']

export const AGENT_INSTRUCTION_KIND_LABELS: Record<
  AgentInstructionKind,
  { en: string; sv: string }
> = {
  product_selection: { en: 'Product selection', sv: 'Produktval' },
  design: { en: 'Design', sv: 'Design' },
  agent: { en: 'Agent', sv: 'Agent' },
  email_inbox: { en: 'Email inbox', sv: 'Inkorg' },
}

export const useAgentInstructionsApi = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const { toast } = useToast()
  const { t } = useLocalization()

  const { data, isPending } = useQuery({
    queryKey: [queryKeys.agentInstructions],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_instruction')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
    enabled,
  })

  const createInstruction = useMutation({
    mutationFn: async (
      values: Omit<InsertAgentInstruction, 'created_by'> & {
        created_by?: string | null
      }
    ) => {
      const { data, error } = await supabase
        .from('agent_instruction')
        .insert({
          ...values,
          created_by: values.created_by ?? session?.user?.id ?? null,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.agentInstructions],
      })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('Could not save instruction', 'Kunde inte spara instruktionen'),
        description: error.message,
      })
    },
  })

  const updateInstruction = useMutation({
    mutationFn: async ({
      id,
      ...values
    }: { id: string } & Database['public']['Tables']['agent_instruction']['Update']) => {
      const { data, error } = await supabase
        .from('agent_instruction')
        .update(values)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.agentInstructions],
      })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t(
          'Could not update instruction',
          'Kunde inte uppdatera instruktionen'
        ),
        description: error.message,
      })
    },
  })

  return {
    instructions: data ?? [],
    isPending,
    createInstruction,
    updateInstruction,
  }
}
